import React from 'react';
import { Megaphone, X } from 'lucide-react';

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Megaphone bell — SuperAdmin-published system broadcasts.
 * Pure presentational; lifecycle (fetch + viewed/dismissed) lives in TopBar.
 *
 * Props:
 *  - items: the visible (non-dismissed) broadcast list
 *  - unreadCount: badge value — drops to 0 after the dropdown is opened
 *  - onDismiss(id): remove one broadcast from the list permanently
 *  - getId(b): derive a stable id for the dismiss key
 */
export const BroadcastsMenu = React.forwardRef(function BroadcastsMenu(
  { open, onToggle, items, unreadCount, onDismiss, getId = (b, i) => b?.id || String(i) },
  ref,
) {
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        title="Broadcasts from Rupiwise"
        data-testid="topbar-broadcasts-btn"
        className="relative h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        style={{ color: 'var(--theme-primary)' }}
      >
        <Megaphone className="w-4 h-4" strokeWidth={1.8} />
        {unreadCount > 0 && (
          <span
            data-testid="topbar-broadcasts-badge"
            className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-slate-800"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-96 max-w-[92vw] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl py-1.5 z-50"
          data-testid="topbar-broadcasts-menu"
        >
          <div className="px-3 py-2 flex items-center justify-between border-b border-slate-100 dark:border-slate-700">
            <div>
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Broadcasts from Rupiwise</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Updates &amp; offers from your administrator</div>
            </div>
            <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-200">
              {items.length}
            </span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Megaphone className="w-8 h-8 text-slate-300 dark:text-slate-500 mb-2" />
                <div className="text-sm text-slate-500 dark:text-slate-400">No broadcasts</div>
              </div>
            ) : (
              items.map((b, idx) => {
                const id = getId(b, idx);
                return (
                  <div
                    key={id}
                    className="group relative px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 border-b border-slate-50 dark:border-slate-700 last:border-b-0"
                    data-testid={`topbar-broadcast-item-${b.type || 'update'}`}
                  >
                    <div className="flex items-start gap-2 pr-6">
                      <span
                        className={`mt-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          b.type === 'offer'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}
                      >
                        {b.type || 'update'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">{b.title}</div>
                        <div className="text-[12px] text-slate-600 dark:text-slate-300 mt-0.5 leading-snug">{b.message}</div>
                        <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">{timeAgo(b.created_at)}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDismiss?.(id); }}
                      aria-label="Dismiss broadcast"
                      title="Dismiss"
                      data-testid={`topbar-broadcast-dismiss-${b.type || 'update'}`}
                      className="absolute top-1.5 right-1.5 h-5 w-5 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 dark:hover:bg-slate-600/60 dark:hover:text-slate-100 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" strokeWidth={2.5} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default BroadcastsMenu;
